<template>
  <div class="content-manager">

    <div class="d-flex content-container">
      <!-- Left Sidebar: File Explorer -->
      <div class="file-explorer" :class="{ 'collapsed': !fileExplorerExpanded }" :style="{ width: fileExplorerWidth + 'px' }">
        <!-- Expanded View -->
        <div v-if="fileExplorerExpanded" class="expanded-view">
          <div class="d-flex flex-row content-header pa-4 border-bottom align-center">  
            <v-btn 
              icon 
              @click="explorerViewMode = explorerViewMode === 'states' ? 'files' : 'states'" 
              :title="explorerViewMode === 'states' ? 'Switch to File View' : 'Switch to State View'"
            >
              <v-icon>{{ explorerViewMode === 'states' ? 'mdi-file-tree' : 'mdi-state-machine' }}</v-icon>
            </v-btn>
            File Explorer
            <v-spacer></v-spacer>
            
            <v-btn 
              icon 
              @click="importFromDrive" 
              title="Import from Google Drive"
              :loading="importing"
              :disabled="importing"
            >
              <v-icon>mdi-cloud-download</v-icon>
            </v-btn>
            <v-btn icon @click="fileExplorerExpanded = false" title="Collapse File Explorer">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
          </div>
          
          <div class="pa-3">
            <v-text-field
              v-model="fileSearch"
              placeholder="Search files..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              clearable
            ></v-text-field>
          </div>
          
          <v-divider></v-divider>
          
          <div class="file-tree pa-2">
            <!-- State-based view -->
            <div v-if="explorerViewMode === 'states'">
              <v-expansion-panels 
                v-model="expandedStates" 
                multiple 
                flat 
                accordion
                class="workflow-states"
              >
                <v-expansion-panel 
                  v-for="state in workflowStates" 
                  :key="state"
                  class="state-panel"
                >
                  <v-expansion-panel-header class="pa-3">
                    <div class="d-flex align-center">
                      <v-icon left color="primary" small>{{ getStateIcon(state) }}</v-icon>
                      <span class="text-subtitle-2 font-weight-medium">{{ state }}</span>
                      <v-spacer></v-spacer>
                      <v-chip 
                        small 
                        :color="stateItemCounts[state] > 0 ? 'primary' : 'grey lighten-2'"
                        :text-color="stateItemCounts[state] > 0 ? 'white' : 'grey'"
                      >
                        {{ stateItemCounts[state] }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  
                  <v-expansion-panel-content class="pa-0">
                    <v-list dense v-if="contentItemsByState[state].length > 0">
                      <v-list-item-group v-model="selectedFile">
                                                 <v-list-item 
                           v-for="item in contentItemsByState[state]" 
                           :key="item.id" 
                           :value="item.id"
                           class="file-item"
                           :class="{ 'has-pending-changes': item.hasPendingChanges }"
                         >
                          <v-list-item-icon>
                            <v-icon small>{{ getFileIcon(item.mimeType || item.fileName) }}</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text-body-2">
                              {{ item.fileName }}
                              <v-chip 
                                v-if="item.hasPendingChanges" 
                                x-small 
                                color="orange" 
                                text-color="white"
                                class="ml-1 pending-changes-chip"
                              >
                                Pending Changes
                              </v-chip>
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                              v{{ item.currentVersion || 1 }} • {{ formatDate(item.lastUpdatedAt) }}
                              <span v-if="item.hasPendingChanges" class="orange--text">
                                • Changes detected {{ formatDate(item.pendingChanges?.detectedAt) }}
                              </span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn 
                                  icon 
                                  x-small 
                                  v-bind="attrs" 
                                  v-on="on"
                                  @click.stop
                                >
                                  <v-icon small>mdi-dots-vertical</v-icon>
                                </v-btn>
                              </template>
                              <v-list dense>
                                <!-- Pending Changes Actions -->
                                <template v-if="item.hasPendingChanges">
                                  <v-list-item @click="acceptPendingChanges(item)">
                                    <v-list-item-icon>
                                      <v-icon small color="success">mdi-check</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="text-caption">Accept Changes</v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item @click="rejectPendingChanges(item)">
                                    <v-list-item-icon>
                                      <v-icon small color="error">mdi-close</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="text-caption">Reject Changes</v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item @click="showPendingChangesDiff(item)">
                                    <v-list-item-icon>
                                      <v-icon small color="info">mdi-compare</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                      <v-list-item-title class="text-caption">View Changes</v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-divider></v-divider>
                                </template>
                                
                                <!-- Regular State Actions -->
                                <v-list-item @click="moveToNextState(item)" :disabled="item.hasPendingChanges">
                                  <v-list-item-icon>
                                    <v-icon small>mdi-arrow-right</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <v-list-item-title class="text-caption">Move Forward</v-list-item-title>
                                  </v-list-item-content>
                                </v-list-item>
                                <v-list-item @click="moveToPreviousState(item)" :disabled="item.hasPendingChanges">
                                  <v-list-item-icon>
                                    <v-icon small>mdi-arrow-left</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <v-list-item-title class="text-caption">Move Back</v-list-item-title>
                                  </v-list-item-content>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                    <div v-else class="text-center pa-4">
                      <v-icon color="grey lighten-2">mdi-inbox-outline</v-icon>
                      <div class="text-caption text--secondary mt-1">No documents in this state</div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            
            <!-- File-based view (original) -->
            <div v-else>
              <v-list dense>
                <v-list-item-group v-model="selectedFile">
                  <v-list-item 
                    v-for="item in filteredContentItems" 
                    :key="item.id" 
                    :value="item.id"
                  >
                    <v-list-item-icon>
                      <v-icon>{{ getFileIcon(item.mimeType || item.fileName) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.fileName }}</v-list-item-title>
                      <v-list-item-subtitle class="d-flex align-center text-caption text--secondary">
                        {{ item.currentState || 'Imported' }} • v{{ item.currentVersion || 1 }} • {{ formatDate(item.lastUpdatedAt) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </div>
            
            <!-- Empty state -->
            <div v-if="filteredContentItems.length === 0" class="text-center pa-4">
              <v-icon size="48" color="grey lighten-2">mdi-folder-open-outline</v-icon>
              <div class="text-caption text--secondary mt-2">
                {{ fileSearch ? 'No files match your search' : 'No files imported yet' }}
              </div>
              <v-btn 
                v-if="!fileSearch" 
                small 
                text 
                color="primary" 
                @click="importFromDrive"
                :loading="importing"
                class="mt-2"
              >
                <v-icon left small>mdi-cloud-download</v-icon>
                Import Files
              </v-btn>
            </div>
          </div>
        </div>
        
        <!-- Collapsed View -->
        <div v-else class="collapsed-view">
          <div class="d-flex flex-row content-header pa-4 border-bottom align-center">  
            <v-btn icon @click="fileExplorerExpanded = true" title="Expand File Explorer" class="mb-2">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          
          <v-divider></v-divider>
          
          <div class="collapsed-files pa-1">
            <v-tooltip right v-for="file in recentFiles" :key="file.id">
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon 
                  small 
                  class="d-block mb-1" 
                  @click="selectedFile = file.id"
                  :color="selectedFile === file.id ? 'primary' : ''"
                  v-bind="attrs" 
                  v-on="on"
                >
                  <v-icon small>{{ file.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ file.name }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content flex-grow-1">
        <div class="content-header pa-4 border-bottom">
          <div class="d-flex align-center">
            <div v-if="selectedFileObject" class="mr-4">
              <div v-if="selectedFileHasPendingChanges" class="text-caption orange--text mt-1">
                <v-icon small color="orange">mdi-alert</v-icon>
                Resolve pending changes to modify state
              </div>
            </div>
            <div class="flex-grow-1">
              <h2 class="text-h5 font-weight-medium mb-1">
                {{ selectedFileName || '' }}
                <v-chip 
                  v-if="selectedFileHasPendingChanges" 
                  small 
                  color="orange" 
                  text-color="white"
                  class="ml-2"
                >
                  <v-icon left small>mdi-alert</v-icon>
                  Pending Changes
                </v-chip>
              </h2>
              
              <div v-if="selectedFileHasPendingChanges" class="mt-2">
                <v-alert 
                  dense 
                  outlined 
                  type="warning"
                  class="mb-0"
                >
                  <div class="d-flex align-center">
                    <div class="flex-grow-1">
                      This document has pending changes from Google Drive that need review.
                    </div>
                    <v-btn 
                      small 
                      outlined 
                      color="orange" 
                      @click="showPendingChangesDiff(selectedFileObject)"
                      class="ml-2"
                    >
                      Review Changes
                    </v-btn>
                  </div>
                </v-alert>
              </div>
            </div>
            
            <div class="d-flex align-center">
              <v-btn-toggle v-model="viewMode" mandatory dense>
                <v-btn value="view">
                  <v-icon>mdi-eye</v-icon>
                  View
                </v-btn>
                <v-btn value="edit">
                  <v-icon>mdi-pencil</v-icon>
                  Edit
                </v-btn>
              </v-btn-toggle>
            </div>

          </div>
        </div>

        <!-- Tool Bar-->
        <div v-if="selectedFileObject" class="d-flex flex-row content-header pa-4">
          <div class="d-flex align-center justify-center">
            <v-chip 
              :color="getStateColor(selectedFileObject.currentState || 'Imported')" 
              text-color="white"
              class="mr-2"
            >
              <v-icon left small>{{ getStateIcon(selectedFileObject.currentState || 'Imported') }}</v-icon>
              {{ selectedFileObject.currentState || 'Imported' }}
            </v-chip>
            
            <!-- Historical Version Indicator -->
            <v-chip 
              v-if="viewingHistoricalVersion"
              color="orange" 
              text-color="white"
              class="ml-2"
              @click="restoreToCurrent"
              clickable
            >
              <v-icon left small>mdi-history</v-icon>
              Viewing: {{ currentViewingState }}
              <v-icon right small>mdi-restore</v-icon>
            </v-chip>
          </div>
          <v-spacer></v-spacer>
                     <div class="text-caption text--secondary">
             {{ selectedFileInfo }}
             <span v-if="hasUnsavedChanges" class="orange--text ml-2">
               <v-icon small color="orange">mdi-circle</v-icon>
               Unsaved changes
             </span>
           </div>
          <v-spacer></v-spacer>
          
          <!-- AI Rewrite Button (only show in AI Rewrite state) -->
          <v-btn 
            v-if="selectedFileObject && selectedFileObject.currentState === 'AI Rewrite'"
            color="purple" 
            :disabled="selectedFileHasPendingChanges || aiRewriting"
            :loading="aiRewriting"
            @click="startAIRewrite"
            class="mr-2"
            :dark="!aiRewriting"
          >
            <v-icon left>mdi-robot</v-icon>
            Generate AI Options
          </v-btn>
          
          <v-btn 
            color="primary" 
            :disabled="!hasUnsavedChanges || !selectedFileObject"
            :loading="savingContent"
            @click="saveContent"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </div>
        
        <div class="content-body pa-4">
          <div v-if="!selectedFile" class="text-center pa-8">
            <v-icon size="64" color="grey lighten-2">mdi-file-document-plus-outline</v-icon>
            <div class="text-h6 mt-4 text--secondary">
              No file selected
            </div>
            <div class="text-body-2 text--secondary">
              Choose a file from the explorer to view its content
            </div>
          </div>
          
          <div v-else class="content-viewer">
            <v-card flat class="content-card">
              <v-card-text class="pa-6">
                <div v-if="viewMode === 'view'" class="content-display">
                  <div class="text-body-1 line-height-relaxed content-text" v-html="formatContent(currentContent)">
                  </div>
                </div>
                
                <div v-else class="content-editor">
                  <v-textarea
                    v-model="currentContent"
                    placeholder="Start typing your content..."
                    outlined
                    auto-grow
                    rows="20"
                    hide-details
                  ></v-textarea>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>

      <!-- Right Sidebar: Versions -->
      <div class="versions-panel" :class="{ 'collapsed': !versionsExpanded }" :style="{ width: versionsWidth + 'px' }">
        <!-- Expanded View -->
        <div v-if="versionsExpanded" class="expanded-view">
          <div class="d-flex flex-row content-header pa-4 border-bottom align-center">
            <v-btn icon @click="versionsExpanded = false" title="Collapse State History">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-icon left color="black">mdi-history</v-icon>
            State History
          </div>

          <div v-if="selectedFileObject" class="pa-3">
            <v-btn :disabled="!canMoveToNextState || selectedFileHasPendingChanges" @click="moveToNextState(selectedFileObject)" block color="green" dark><v-icon left>mdi-check</v-icon>Approve</v-btn>
            <v-btn :disabled="!canMoveToPreviousState || selectedFileHasPendingChanges" @click="moveToPreviousState(selectedFileObject)" class="mt-2" block color="grey lighten-2"><v-icon left>mdi-close</v-icon>Reject</v-btn>
          </div>
          
          <v-divider></v-divider>
          
          <div class="pa-3">
            <v-text-field
              v-model="historySearch"
              placeholder="Search state history..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              clearable
            ></v-text-field>
          </div>
          
          <v-divider></v-divider>
          
          <div class="versions-list pa-2">
            <div v-if="!selectedFile" class="text-center pa-4">
              <v-icon size="48" color="grey lighten-2">mdi-history</v-icon>
              <div class="text-caption text--secondary mt-2">
                Select a document to view its state history
              </div>
            </div>
            
            <div v-else-if="loadingStateHistory" class="text-center pa-4">
              <v-progress-circular indeterminate size="24"></v-progress-circular>
              <div class="text-caption text--secondary mt-2">
                Loading state history...
              </div>
            </div>
            
            <v-list dense v-else-if="stateHistory.length > 0">
              <v-list-item 
                v-for="(entry, index) in filteredStateHistory" 
                :key="entry.id"
                class="state-history-item"
                :class="{ 'viewing-version': viewingHistoricalVersion && currentViewingState === entry.toState }"
                @click="loadHistoricalVersion(entry)"
                clickable
              >
                <v-list-item-avatar size="32">
                  <v-icon :color="index === 0 ? 'success' : 'grey'">
                    {{ getStateIcon(entry.toState) }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ entry.toState }}
                    <span v-if="index === 0" class="text-caption text--secondary">(Current)</span>
                    <span v-if="viewingHistoricalVersion && currentViewingState === entry.toState" class="text-caption success--text ml-2">
                      <v-icon small>mdi-eye</v-icon> Viewing
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ entry.fromState ? `From ${entry.fromState}` : 'Initial state' }} •
                    {{ formatDate(entry.changedAt) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="entry.reason" class="text-caption text--secondary">
                    {{ entry.reason }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            
            <div v-else class="text-center pa-4">
              <v-icon color="grey lighten-2">mdi-inbox-outline</v-icon>
              <div class="text-caption text--secondary mt-1">No state history available</div>
            </div>
          </div>
        </div>
        
        <!-- Collapsed View -->
        <div v-else class="collapsed-view">
          <div class="d-flex flex-row content-header pa-4 border-bottom align-center">  
            <v-btn icon @click="versionsExpanded = true" title="Expand State History" class="mb-2">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
          </div>
          
          <v-divider></v-divider>
          
          <div class="collapsed-versions pa-1">
            <v-tooltip left v-for="(entry, index) in recentStateHistory" :key="entry.id">
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon 
                  small 
                  class="d-block mb-1" 
                  :color="index === 0 ? 'success' : 'grey lighten-1'"
                  v-bind="attrs" 
                  v-on="on"
                >
                  <v-icon small>{{ getStateIcon(entry.toState) }}</v-icon>
                </v-btn>
              </template>
              <span>{{ entry.toState }} • {{ formatDate(entry.changedAt) }}</span>
            </v-tooltip>
            
            <div v-if="!selectedFile" class="text-center pa-2">
              <v-icon small color="grey lighten-2">mdi-history</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Pending Changes Dialog -->
    <v-dialog 
      v-model="showPendingChangesDialog" 
      max-width="800px"
      scrollable
    >
      <v-card v-if="selectedItemForChanges">
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange">mdi-compare</v-icon>
          Pending Changes: {{ selectedItemForChanges.fileName }}
          <v-spacer></v-spacer>
          <v-btn icon @click="showPendingChangesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-0">
          <v-tabs v-model="pendingChangesTab">
            <v-tab>Current Content</v-tab>
            <v-tab>New Content</v-tab>
            <v-tab>Details</v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="pendingChangesTab">
            <!-- Current Content -->
            <v-tab-item>
              <div class="pa-4">
                <div class="text-subtitle-2 mb-2">
                  Current Version (v{{ selectedItemForChanges.currentVersion || 1 }})
                </div>
                <v-card outlined class="pa-3" style="max-height: 400px; overflow-y: auto;">
                  <pre class="text-body-2">{{ getCurrentContent() }}</pre>
                </v-card>
              </div>
            </v-tab-item>
            
            <!-- New Content -->
            <v-tab-item>
              <div class="pa-4">
                <div class="text-subtitle-2 mb-2">
                  Pending Content from Google Drive
                </div>
                <v-card outlined class="pa-3" style="max-height: 400px; overflow-y: auto;">
                  <pre class="text-body-2">{{ selectedItemForChanges.pendingChanges?.newContent || 'No content available' }}</pre>
                </v-card>
              </div>
            </v-tab-item>
            
            <!-- Details -->
            <v-tab-item>
              <div class="pa-4">
                <div class="text-subtitle-2 mb-3">Change Details</div>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-medium">Source:</td>
                      <td>{{ selectedItemForChanges.pendingChanges?.source || 'Unknown' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Detected:</td>
                      <td>{{ formatDate(selectedItemForChanges.pendingChanges?.detectedAt) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Reason:</td>
                      <td>{{ selectedItemForChanges.pendingChanges?.reason || 'Content updated' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Status:</td>
                      <td>{{ selectedItemForChanges.pendingChanges?.newContentStatus || 'Unknown' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Current State:</td>
                      <td>
                        <v-chip small :color="getStateColor(selectedItemForChanges.currentState)">
                          {{ selectedItemForChanges.currentState || 'Imported' }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-btn 
            color="success" 
            @click="acceptPendingChanges(selectedItemForChanges); showPendingChangesDialog = false"
          >
            <v-icon left>mdi-check</v-icon>
            Accept Changes
          </v-btn>
          
          <v-btn 
            color="error" 
            @click="rejectPendingChanges(selectedItemForChanges); showPendingChangesDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            Reject Changes
          </v-btn>
          
          <v-spacer></v-spacer>
          
          <v-btn 
            text 
            @click="showPendingChangesDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- AI Options Dialog -->
    <v-dialog 
      v-model="showAIOptionsDialog" 
      max-width="1200px"
      scrollable
    >
      <v-card v-if="aiOptions.length > 0">
        <v-card-title class="d-flex align-center">
          <v-icon left color="purple">mdi-robot</v-icon>
          AI Rewrite Options: {{ selectedFileName }}
          <v-spacer></v-spacer>
          <v-btn icon @click="showAIOptionsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-0">
          <v-tabs v-model="selectedAIOption">
            <v-tab v-for="(option, index) in aiOptions" :key="option.id">
              <v-icon left small>{{ getApproachIcon(option.approach) }}</v-icon>
              {{ option.approach.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
            </v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="selectedAIOption">
            <v-tab-item v-for="option in aiOptions" :key="option.id">
              <div class="pa-4">
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="text-subtitle-2">
                    {{ option.approach.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }} Approach
                  </div>
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="option.confidence > 0.7 ? 'success' : option.confidence > 0.4 ? 'warning' : 'error'"
                    >
                      {{ Math.round(option.confidence * 100) }}% confidence
                    </v-chip>
                  </div>
                </div>
                
                <v-card outlined class="pa-4" style="max-height: 500px; overflow-y: auto;">
                  <div v-if="option.error" class="text-center py-4">
                    <v-icon color="error" size="48">mdi-alert-circle</v-icon>
                    <div class="text-h6 mt-2 error--text">Error Generating Content</div>
                    <div class="text-body-2 text--secondary">{{ option.error }}</div>
                  </div>
                  <div v-else class="content-preview">
                    <div v-html="formatContent(option.content)"></div>
                  </div>
                </v-card>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-btn 
            text 
            @click="showAIOptionsDialog = false"
          >
            Cancel
          </v-btn>
          
          <v-spacer></v-spacer>
          
          <v-btn 
            large
            color="success"
            @click="selectCurrentAIOption"
            :disabled="!aiOptions[selectedAIOption] || !!aiOptions[selectedAIOption]?.error"
          >
            <v-icon left>mdi-check</v-icon>
            Use This Version
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Success Snackbar -->
    <v-snackbar v-model="successSnackbar" color="success">
      {{ successMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="successSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
    
    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { getFirestore, collection, onSnapshot, orderBy, query, doc, getDoc, getDocs, limit, addDoc, updateDoc, Timestamp } from 'firebase/firestore'
import axios from 'axios'
import { marked } from 'marked'

export default {
  name: 'ContentManager',
  
  data() {
    return {
      // Sidebar visibility
      fileExplorerExpanded: true,
      versionsExpanded: true,
      
      // Search filters
      fileSearch: '',
      historySearch: '',
      
      // Selection states
      selectedFile: null,
      selectedVersion: null,
      
      // Content
      currentContent: 'This is sample content for the selected file. In the actual implementation, this would be loaded dynamically based on the selected file and version.',
      
      // View mode
      viewMode: 'view',
      
      // Import state
      importing: false,
      contentItems: [],
      unsubscribe: null,
      
      // State history
      stateHistory: [],
      loadingStateHistory: false,
      viewingHistoricalVersion: false,
      currentViewingState: null,
      
      // Pending changes
      showPendingChangesDialog: false,
      selectedItemForChanges: null,
      pendingChangesTab: 0,
      
      // Workflow states configuration
      workflowStates: [
        'Imported',
        'Ready',
        'AI Rewrite',
        'Marketing Reviewed',
        'Page Owner (Rev 1)',
        'Marketing (Rev 1)',
        'Joint Review',
        'Marketing Final',
        'Ascribe Final',
        'Translation',
        'Translation (Rev 1)',
        'Translation Final'
      ],
      
      // Current view mode for File Explorer
      explorerViewMode: 'states', // 'states' or 'files'
      
      // Expanded states in the workflow panels
      expandedStates: [], // All states collapsed by default
      
      // Sample data for collapsed views
      recentFiles: [
        { id: 'example1', name: 'Sample Document 1.md', icon: 'mdi-file-document-outline' },
        { id: 'example2', name: 'Sample Document 2.md', icon: 'mdi-file-document-outline' },
        { id: 'example3', name: 'Template.docx', icon: 'mdi-file-word-outline' },
        { id: 'example4', name: 'Guide.pdf', icon: 'mdi-file-pdf-box' }
      ],
      
      recentVersions: [
        { id: 'v1', name: 'v1.0.0 (Current)', icon: 'mdi-check-circle', iconColor: 'success' },
        { id: 'v2', name: 'v0.9.5', icon: 'mdi-source-commit', iconColor: 'info' },
        { id: 'v3', name: 'v0.9.0', icon: 'mdi-source-commit', iconColor: 'grey' }
      ],
      
      // Notification system
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false,
      successMessage: '',
      
      // Content editing state
      savingContent: false,
      originalContent: '', // Track original content to detect changes
      
      // AI Rewrite state
      aiRewriting: false,
      showAIOptionsDialog: false,
      aiOptions: [],
      selectedAIOption: null
    }
  },
  
  computed: {
    selectedFileName() {
      if (!this.selectedFile) return null
      const selectedItem = this.contentItems.find(item => item.id === this.selectedFile)
      return selectedItem ? selectedItem.fileName : null
    },
    
    selectedFileInfo() {
      if (!this.selectedFile) return ''
      const selectedItem = this.contentItems.find(item => item.id === this.selectedFile)
      if (!selectedItem) return ''
      
      const lastModified = this.formatDate(selectedItem.lastUpdatedAt)
      const version = selectedItem.currentVersion || 1
      const state = selectedItem.currentState || 'Imported'
      return `${state} • v${version} • Last modified: ${lastModified}`
    },
    
    selectedFileObject() {
      if (!this.selectedFile) return null
      return this.contentItems.find(item => item.id === this.selectedFile)
    },
    
    selectedFileHasPendingChanges() {
      const selectedItem = this.selectedFileObject
      return selectedItem ? selectedItem.hasPendingChanges : false
    },
    
    filteredContentItems() {
      if (!this.fileSearch) return this.contentItems
      
      const search = this.fileSearch.toLowerCase()
      return this.contentItems.filter(item => 
        item.fileName?.toLowerCase().includes(search) ||
        item.folderPath?.toLowerCase().includes(search)
      )
    },
    
    contentItemsByState() {
      const grouped = {}
      
      // Initialize all states with empty arrays
      this.workflowStates.forEach(state => {
        grouped[state] = []
      })
      
      // Group filtered items by their current state
      this.filteredContentItems.forEach(item => {
        const state = item.currentState || 'Imported'
        if (grouped[state]) {
          grouped[state].push(item)
        }
      })
      
      return grouped
    },
    
    stateItemCounts() {
      const counts = {}
      this.workflowStates.forEach(state => {
        counts[state] = this.contentItemsByState[state]?.length || 0
      })
      return counts
    },
    
    filteredStateHistory() {
      if (!this.historySearch) return this.stateHistory
      
      const search = this.historySearch.toLowerCase()
      return this.stateHistory.filter(entry => 
        entry.toState?.toLowerCase().includes(search) ||
        entry.fromState?.toLowerCase().includes(search) ||
        entry.reason?.toLowerCase().includes(search) ||
        entry.changedBy?.toLowerCase().includes(search)
      )
    },
    
    recentStateHistory() {
      return this.stateHistory.slice(0, 5) // Show last 5 state changes in collapsed view
    },
    
    fileExplorerWidth() {
      return this.fileExplorerExpanded ? 300 : 60
    },
    
    versionsWidth() {
      return this.versionsExpanded ? 280 : 60
    },
    
    canMoveToNextState() {
      if (!this.selectedFileObject) return false
      const currentState = this.selectedFileObject.currentState || 'Imported'
      const currentIndex = this.workflowStates.indexOf(currentState)
      return currentIndex >= 0 && currentIndex < this.workflowStates.length - 1
    },
    
    canMoveToPreviousState() {
      if (!this.selectedFileObject) return false
      const currentState = this.selectedFileObject.currentState || 'Imported'
      const currentIndex = this.workflowStates.indexOf(currentState)
      return currentIndex > 0
    },
    
    hasUnsavedChanges() {
      return this.currentContent !== this.originalContent && this.currentContent && this.originalContent
    }
  },
  
  methods: {
    toggleFileExplorer() {
      this.fileExplorerExpanded = !this.fileExplorerExpanded
    },
    
    toggleVersions() {
      this.versionsExpanded = !this.versionsExpanded
    },
    
    async importFromDrive() {
      try {
        this.importing = true
        
        // Show loading message
        this.showSuccess('Starting import from Google Drive...')
        
        // Call the import function
        const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/manual_content_manager_import')
        
        if (response.data.success) {
          this.showSuccess(`Import completed! ${response.data.message}`)
          
          // Log detailed results
          console.log('Import results:', response.data)
          
          // Files will be automatically updated via the real-time listener
        } else {
          this.showError('Import failed: ' + (response.data?.message || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('Import error:', error)
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error occurred'
        this.showError('Import failed: ' + errorMessage)
      } finally {
        this.importing = false
      }
    },
    
    setupRealtimeListener() {
      try {
        const db = getFirestore()
        const contentCollection = collection(db, 'comms_content_manager')
        const q = query(contentCollection, orderBy('fileName', 'asc'))
        
        // Set up real-time listener
        this.unsubscribe = onSnapshot(q, (snapshot) => {
          this.contentItems = []
          snapshot.forEach((doc) => {
            this.contentItems.push({
              id: doc.id,
              ...doc.data()
            })
          })
          
          // Update sample files for collapsed view
          this.recentFiles = this.contentItems.slice(0, 4).map(item => ({
            id: item.id,
            name: item.fileName,
            icon: this.getFileIcon(item.mimeType || item.fileName)
          }))
          
          console.log(`Loaded ${this.contentItems.length} files from Content Manager`)
        }, (error) => {
          console.error('Error in real-time listener:', error)
          this.showError('Failed to sync with database. Please refresh the page.')
        })
        
      } catch (error) {
        console.error('Error setting up real-time listener:', error)
        this.showError('Failed to connect to database. Please try again.')
      }
    },
    
    getFileIcon(mimeTypeOrFileName) {
      if (!mimeTypeOrFileName) return 'mdi-file-document-outline'
      
      const fileName = mimeTypeOrFileName.toLowerCase()
      
      if (mimeTypeOrFileName.includes('google-apps.document') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
        return 'mdi-file-word-outline'
      } else if (fileName.endsWith('.pdf')) {
        return 'mdi-file-pdf-box'
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        return 'mdi-file-excel-outline'
      } else if (fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
        return 'mdi-file-powerpoint-outline'
      } else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
        return 'mdi-file-document-outline'
      } else {
        return 'mdi-file-outline'
      }
    },
    
    getStateIcon(state) {
      const stateIcons = {
        'Imported': 'mdi-cloud-download',
        'Ready': 'mdi-check-circle-outline',
        'AI Rewrite': 'mdi-robot',
        'Marketing Reviewed': 'mdi-account-check',
        'Page Owner (Rev 1)': 'mdi-account-edit',
        'Marketing (Rev 1)': 'mdi-marker-check',
        'Joint Review': 'mdi-account-group',
        'Marketing Final': 'mdi-check-circle',
        'Ascribe Final': 'mdi-shield-check',
        'Translation': 'mdi-translate',
        'Translation (Rev 1)': 'mdi-translate-variant',
        'Translation Final': 'mdi-check-all'
      }
      return stateIcons[state] || 'mdi-circle'
    },
    
    async moveToNextState(item) {
      const currentIndex = this.workflowStates.indexOf(item.currentState || 'Imported')
      if (currentIndex < this.workflowStates.length - 1) {
        const nextState = this.workflowStates[currentIndex + 1]
        await this.changeDocumentState(item, nextState, 'Moved forward in workflow')
      } else {
        this.showSuccess('Document is already in the final state')
      }
    },
    
    async moveToPreviousState(item) {
      const currentIndex = this.workflowStates.indexOf(item.currentState || 'Imported')
      if (currentIndex > 0) {
        const previousState = this.workflowStates[currentIndex - 1]
        await this.changeDocumentState(item, previousState, 'Moved back in workflow')
      } else {
        this.showSuccess('Document is already in the initial state')
      }
    },
    
    async changeDocumentState(item, newState, reason) {
      try {
        this.showSuccess(`Moving "${item.fileName}" to "${newState}"...`)
        
        // Call the state change API
        const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/change_document_state', {
          documentId: item.id,
          toState: newState,
          reason: reason,
          changedBy: this.$store.getters.currentUser?.email || 'unknown'
        })
        
        if (response.data.success) {
          this.showSuccess(`Document moved to "${newState}"`)
          
          // The real-time listener will automatically update the UI
          console.log('State change successful:', response.data)
        } else {
          this.showError('State change failed: ' + (response.data?.message || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('Error changing document state:', error)
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error occurred'
        this.showError('Failed to change document state: ' + errorMessage)
      }
    },
    
    async acceptPendingChanges(item) {
      try {
        this.showSuccess(`Accepting pending changes for "${item.fileName}"...`)
        
        const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/accept_pending_changes', {
          documentId: item.id,
          acceptedBy: this.$store.getters.currentUser?.email || 'unknown',
          reason: 'Pending changes accepted from workflow interface'
        })
        
        if (response.data.success) {
          this.showSuccess(`Pending changes accepted! Document updated to v${response.data.newVersion}`)
        } else {
          this.showError('Failed to accept changes: ' + (response.data?.message || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('Error accepting pending changes:', error)
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error occurred'
        this.showError('Failed to accept pending changes: ' + errorMessage)
      }
    },
    
    async rejectPendingChanges(item) {
      try {
        // Show confirmation dialog
        const confirmed = await this.$confirm('Are you sure you want to reject the pending changes? This action cannot be undone.', {
          title: 'Reject Pending Changes',
          buttonTrueText: 'Reject',
          buttonFalseText: 'Cancel',
          buttonTrueColor: 'error',
          buttonFalseColor: 'grey'
        })
        
        if (!confirmed) return
        
        this.showSuccess(`Rejecting pending changes for "${item.fileName}"...`)
        
        const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/reject_pending_changes', {
          documentId: item.id,
          rejectedBy: this.$store.getters.currentUser?.email || 'unknown',
          reason: 'Pending changes rejected from workflow interface'
        })
        
        if (response.data.success) {
          this.showSuccess('Pending changes rejected')
        } else {
          this.showError('Failed to reject changes: ' + (response.data?.message || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('Error rejecting pending changes:', error)
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error occurred'
        this.showError('Failed to reject pending changes: ' + errorMessage)
      }
    },
    
    showPendingChangesDiff(item) {
      this.selectedItemForChanges = item
      this.showPendingChangesDialog = true
    },
    
    getCurrentContent() {
      // This would typically load the current version content
      // For now, return placeholder text
      return this.currentContent || 'Current content would be loaded here...'
    },
    
    getStateColor(state) {
      const stateColors = {
        'Imported': 'blue',
        'Ready': 'green',
        'AI Rewrite': 'purple', 
        'Marketing Reviewed': 'teal',
        'Page Owner (Rev 1)': 'indigo',
        'Marketing (Rev 1)': 'pink',
        'Joint Review': 'orange',
        'Marketing Final': 'green',
        'Ascribe Final': 'success',
        'Translation': 'cyan',
        'Translation (Rev 1)': 'lime',
        'Translation Final': 'light-green'
      }
      return stateColors[state] || 'grey'
    },
    
    async loadStateHistory(documentId) {
      if (!documentId) {
        this.stateHistory = []
        return
      }
      
      try {
        this.loadingStateHistory = true
        
        const response = await axios.get('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/get_document_state_history', {
          params: { documentId }
        })
        
        if (response.data.success) {
          this.stateHistory = response.data.history || []
        } else {
          console.error('Failed to load state history:', response.data?.message || 'Unknown error')
          this.stateHistory = []
        }
        
      } catch (error) {
        console.error('Error loading state history:', error)
        this.stateHistory = []
      } finally {
        this.loadingStateHistory = false
      }
    },
    
    async loadFileContent(fileId) {
      try {
        console.log('Loading content for file:', fileId)
        const selectedItem = this.contentItems.find(item => item.id === fileId)
        
        if (!selectedItem) {
          this.currentContent = 'File not found'
          return
        }
        
        // Show loading state
        this.currentContent = `Loading content from: ${selectedItem.fileName}...`
        
        // Get the document reference
        const db = getFirestore()
        const docRef = doc(db, 'comms_content_manager', fileId)
        
        // Get the latest version from the versions subcollection
        const versionsRef = collection(docRef, 'versions')
        const versionsQuery = query(versionsRef, orderBy('versionNumber', 'desc'), limit(1))
        const versionsSnapshot = await getDocs(versionsQuery)
        
        if (!versionsSnapshot.empty) {
          const latestVersion = versionsSnapshot.docs[0].data()
          this.currentContent = latestVersion.content || 'No content available'
          this.originalContent = this.currentContent // Track original content
        } else {
          this.currentContent = 'No content versions found for this document'
          this.originalContent = this.currentContent
        }
        
      } catch (error) {
        console.error('Error loading file content:', error)
        this.currentContent = 'Error loading content: ' + error.message
        this.showError('Failed to load file content')
      }
      
      // Load state history for this document
      this.loadStateHistory(fileId)
    },
    
    async saveContent() {
      if (!this.selectedFileObject || !this.hasUnsavedChanges) {
        return
      }
      
      try {
        this.savingContent = true
        
        const db = getFirestore()
        const docRef = doc(db, 'comms_content_manager', this.selectedFile)
        
        // Get current document data
        const docSnapshot = await getDoc(docRef)
        if (!docSnapshot.exists()) {
          throw new Error('Document not found')
        }
        
        const currentData = docSnapshot.data()
        const newVersionNumber = (currentData.currentVersion || 1) + 1
        
        // Create new version in versions subcollection
        const versionsRef = collection(docRef, 'versions')
        const newVersionData = {
          versionNumber: newVersionNumber,
          content: this.currentContent,
          contentStatus: 'saved',
          createdAt: Timestamp.now(),
          source: 'manual_edit',
          changes: 'Content manually edited and saved',
          state: currentData.currentState || 'Imported',
          editedBy: this.$store.getters.currentUser?.email || 'unknown',
          metadata: {
            editedAt: new Date().toISOString(),
            editedInVersion: newVersionNumber
          }
        }
        
        // Add the new version
        await addDoc(versionsRef, newVersionData)
        
        // Update main document with new version number and last updated time
        await updateDoc(docRef, {
          currentVersion: newVersionNumber,
          lastUpdatedAt: Timestamp.now(),
          lastEditedBy: this.$store.getters.currentUser?.email || 'unknown'
        })
        
        // Update local state
        this.originalContent = this.currentContent
        
        this.showSuccess(`Content saved successfully! Now at version ${newVersionNumber}`)
        console.log(`Content saved as version ${newVersionNumber}`)
        
      } catch (error) {
        console.error('Error saving content:', error)
        this.showError('Failed to save content: ' + error.message)
      } finally {
        this.savingContent = false
      }
    },
    
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown'
      
      // Handle Firestore timestamp
      let date
      if (timestamp.toDate) {
        date = timestamp.toDate()
      } else if (timestamp instanceof Date) {
        date = timestamp
      } else {
        date = new Date(timestamp)
      }
      
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      
      if (diffInSeconds < 60) return 'Just now'
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
      
      return date.toLocaleDateString()
    },
    
    showError(message) {
      this.errorMessage = message
      this.errorSnackbar = true
    },
    
    showSuccess(message) {
      this.successMessage = message
      this.successSnackbar = true
    },
    
    formatContent(content) {
      if (!content) return 'Loading content...'
      
      try {
        // Parse markdown content to HTML for proper display
        return marked.parse(content, { 
          breaks: true, // Convert line breaks to <br>
          gfm: true,    // GitHub flavored markdown
          sanitize: false, // We trust our AI content
          smartypants: true // Smart quotes and dashes
        })
      } catch (error) {
        console.error('Error formatting content:', error)
        // Fallback to simple line break conversion
        return content.replace(/\n/g, '<br>')
      }
    },
    
    async startAIRewrite() {
      if (!this.selectedFileObject || !this.currentContent) {
        this.showError('No content available for rewriting')
        return
      }
      
      try {
        this.aiRewriting = true
        this.showSuccess('Generating AI rewrite options...')
        
        // Call the AI rewriter function
        const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/comms_ai_rewriter', {
          action: 'generate_options',
          contentId: this.selectedFile,
          originalContent: this.currentContent,
          contentType: this.selectedFileObject.mimeType || 'document',
          metadata: {
            fileName: this.selectedFileObject.fileName,
            currentState: this.selectedFileObject.currentState,
            userId: this.$store.getters.currentUser?.email || 'unknown'
          }
        })
        
        if (response.data.success) {
          this.aiOptions = response.data.variations || []
          this.selectedAIOption = 0 // Default to first option
          this.showAIOptionsDialog = true
          
          console.log(`Generated ${this.aiOptions.length} AI options`)
        } else {
          this.showError('Failed to generate AI options: ' + (response.data?.error || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('AI rewrite error:', error)
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error occurred'
        this.showError('Failed to generate AI options: ' + errorMessage)
      } finally {
        this.aiRewriting = false
      }
    },
    
    async selectAIOption(selectedOption) {
      try {
        this.showSuccess(`Using ${selectedOption.approach} version...`)
        
        // Update current content with selected option
        this.currentContent = selectedOption.content
        
        // Record the selection for learning
        await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/comms_ai_rewriter', {
          action: 'record_selection',
          contentId: this.selectedFile,
          selectedVariation: selectedOption,
          metadata: {
            contentType: this.selectedFileObject.mimeType || 'document',
            fileName: this.selectedFileObject.fileName,
            userId: this.$store.getters.currentUser?.email || 'unknown',
            selectionTimestamp: new Date().toISOString()
          }
        })
        
        // Close dialog
        this.showAIOptionsDialog = false
        
        // Show success message
        this.showSuccess(`AI rewrite applied! Content updated with ${selectedOption.approach} approach.`)
        
        console.log(`Selected ${selectedOption.approach} approach by ${selectedOption.model}`)
        
      } catch (error) {
        console.error('Error recording AI selection:', error)
        // Still apply the content even if recording fails
        this.currentContent = selectedOption.content
        this.showAIOptionsDialog = false
        this.showSuccess('AI rewrite applied (selection tracking failed)')
      }
    },
    
    selectCurrentAIOption() {
      // Use the currently selected tab option
      const currentOption = this.aiOptions[this.selectedAIOption]
      if (currentOption && !currentOption.error) {
        this.selectAIOption(currentOption)
      }
    },
    
    getApproachIcon(approach) {
      const icons = {
        professional: 'mdi-tie',
        friendly: 'mdi-heart',
        balanced: 'mdi-scale-balance',
        customer_focused: 'mdi-account-heart'
      }
      return icons[approach] || 'mdi-robot'
    },
    
    getModelColor(model) {
      const colors = {
        gemini: 'blue',
        claude: 'orange',
        openai: 'green'
      }
      return colors[model] || 'grey'
    },
    
    async loadHistoricalVersion(entry) {
      try {
        console.log(`Loading historical version: ${entry.toState}`)
        
        // Get the content for this specific state
        const response = await axios.get('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/get_document_state_content', {
          params: { 
            documentId: this.selectedFile,
            state: entry.toState,
            timestamp: entry.changedAt
          }
        })
        
        if (response.data.success) {
          // Update the displayed content
          this.currentContent = response.data.content || 'No content available for this state'
          this.viewingHistoricalVersion = true
          this.currentViewingState = entry.toState
          
          this.showSuccess(`Now viewing content from ${entry.toState} state`)
        } else {
          this.showError('Failed to load historical content: ' + (response.data?.message || 'Unknown error'))
        }
        
      } catch (error) {
        console.error('Error loading historical version:', error)
        this.showError('Failed to load historical content')
      }
    },
    
    async restoreToCurrent() {
      try {
        // Reload the current version
        await this.loadFileContent(this.selectedFile)
        this.viewingHistoricalVersion = false
        this.currentViewingState = null
        
        this.showSuccess('Restored to current version')
        
      } catch (error) {
        console.error('Error restoring to current:', error)
        this.showError('Failed to restore to current version')
      }
    }
  },
  
  watch: {
    selectedFile(newFile, oldFile) {
      if (newFile) {
        // Reset content tracking when switching files
        this.originalContent = ''
        this.currentContent = ''
        this.loadFileContent(newFile)
      }
    }
  },
  
  mounted() {
    this.setupRealtimeListener()
  },
  
  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  }
}
</script>

<style scoped>
.content-manager {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-toolbar {
  position: relative !important;
  z-index: 1;
}

.content-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.main-content {
  display: flex;
  flex-direction: column;
  min-width: 0; /* Allows flex item to shrink below content size */
  background: #ffffff;
}

.content-header {
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.content-body {
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
}

.content-card {
  max-width: none;
  box-shadow: none !important;
}

.content-display {
  min-height: 400px;
}

.content-editor {
  min-height: 400px;
}

.line-height-relaxed {
  line-height: 1.6;
}

/* Sidebar styling */
.file-explorer, .versions-panel {
  background: #fafafa;
  border-right: 1px solid #e0e0e0;
  height: 100%;
  transition: width 0.3s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.versions-panel {
  border-left: 1px solid #e0e0e0;
  border-right: none;
}

/* Expanded view styling */
.expanded-view {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.file-tree, .versions-list {
  flex: 1;
  overflow-y: auto;
}

/* Collapsed view styling */
.collapsed-view {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.collapsed-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.collapsed-files, .collapsed-versions {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;
  overflow-y: auto;
}

/* Collapsed state adjustments */
.file-explorer.collapsed, .versions-panel.collapsed {
  width: 60px !important;
}

.collapsed-view .v-btn {
  margin: 0 !important;
}

/* Custom scrollbar styling */
.file-tree::-webkit-scrollbar,
.versions-list::-webkit-scrollbar,
.content-body::-webkit-scrollbar,
.collapsed-files::-webkit-scrollbar,
.collapsed-versions::-webkit-scrollbar {
  width: 6px;
}

.file-tree::-webkit-scrollbar-track,
.versions-list::-webkit-scrollbar-track,
.content-body::-webkit-scrollbar-track,
.collapsed-files::-webkit-scrollbar-track,
.collapsed-versions::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.file-tree::-webkit-scrollbar-thumb,
.versions-list::-webkit-scrollbar-thumb,
.content-body::-webkit-scrollbar-thumb,
.collapsed-files::-webkit-scrollbar-thumb,
.collapsed-versions::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.file-tree::-webkit-scrollbar-thumb:hover,
.versions-list::-webkit-scrollbar-thumb:hover,
.content-body::-webkit-scrollbar-thumb:hover,
.collapsed-files::-webkit-scrollbar-thumb:hover,
.collapsed-versions::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Tooltip adjustments for collapsed items */
.collapsed-files .v-btn,
.collapsed-versions .v-btn {
  width: 36px !important;
  height: 36px !important;
}

/* Ensure main content uses available space */
.content-container > .main-content {
  flex: 1 1 auto;
  min-width: 200px; /* Minimum width for content readability */
}

/* Workflow states styling */
.workflow-states {
  background: transparent !important;
}

.state-panel {
  margin-bottom: 4px !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
}

.state-panel .v-expansion-panel-header {
  min-height: 40px !important;
  padding: 8px 12px !important;
}

.state-panel .v-expansion-panel-content {
  background: #fafafa;
}

.file-item {
  background: white;
  border-bottom: 1px solid #f0f0f0;
  margin: 0 4px;
  border-radius: 4px;
}

.file-item:hover {
  background: #f5f5f5;
}

.file-item.v-list-item--active {
  background: #e3f2fd !important;
  border-left: 3px solid #1976d2;
}

/* State transition animations */
.state-panel {
  transition: all 0.3s ease;
}

.file-item {
  transition: all 0.2s ease;
}

/* State history styling */
.state-history-item {
  background: white;
  border-bottom: 1px solid #f0f0f0;
  margin: 0 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.state-history-item:hover {
  background: #f5f5f5;
}

.state-history-item:first-child {
  border-left: 3px solid #4caf50;
}

.state-history-item.viewing-version {
  background: #e8f5e8 !important;
  border-left: 3px solid #4caf50;
}

.state-history-item.viewing-version:hover {
  background: #d4edda !important;
}

/* Pending changes styling */
.file-item.has-pending-changes {
  border-left: 3px solid #ff9800 !important;
  background: #fff3e0 !important;
}

.file-item.has-pending-changes:hover {
  background: #ffe0b2 !important;
}

.pending-changes-chip {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

/* Content text formatting */
.content-text {
  word-wrap: break-word;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 1.6;
  padding: 8px;
  min-height: 400px;
  text-align: left !important;
}

/* Main content display formatting */
.content-display {
  text-align: left !important;
}

.content-display h1,
.content-display h2, 
.content-display h3,
.content-display h4,
.content-display h5,
.content-display h6 {
  text-align: left !important;
  margin: 16px 0 8px 0;
  font-weight: 600;
}

.content-display p {
  text-align: left !important;
  margin: 8px 0;
}

.content-display ul,
.content-display ol {
  text-align: left !important;
  margin: 8px 0;
  padding-left: 24px;
}

.content-display li {
  text-align: left !important;
  margin: 4px 0;
}

.content-display strong {
  font-weight: 600;
}

.content-display em {
  font-style: italic;
}

/* AI Options content preview styling */
.content-preview {
  text-align: left !important;
}

.content-preview h1,
.content-preview h2, 
.content-preview h3,
.content-preview h4,
.content-preview h5,
.content-preview h6 {
  text-align: left !important;
  margin: 16px 0 8px 0;
  font-weight: 600;
}

.content-preview p {
  text-align: left !important;
  margin: 8px 0;
}

.content-preview ul,
.content-preview ol {
  text-align: left !important;
  margin: 8px 0;
  padding-left: 24px;
}

.content-preview li {
  text-align: left !important;
  margin: 4px 0;
}

.content-preview strong {
  font-weight: 600;
}

.content-preview em {
  font-style: italic;
}

/* Make sure textarea matches the view styling */
.content-editor .v-textarea {
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 1.6;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>